import { Injectable } from "@angular/core";
import { DataConverterService } from "@app/shared/services/converter/data-converter.service";
import { EquatorClareHomeService } from "@app/shared/services/equator-api/equator-clarehome/equator-clarehome.service";
import { EquatorDevicesService } from "@app/shared/services/equator-api/equator-devices";
import { EquatorServicesService } from "@app/shared/services/equator-api/equator-services";
import { Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { CHDevice, CHDeviceHelper, CHSecurityDevice } from "../model/ch-device";
import { CHService } from "../model/ch-service";

@Injectable({
    providedIn: "root",
})
export class CHServiceService {
    constructor(
        private readonly equatorClareHomeService: EquatorClareHomeService,
        private readonly equatorServiceService: EquatorServicesService,
        private readonly equatorDeviceService: EquatorDevicesService,
        private readonly dataConverter: DataConverterService
    ) {}

    public getLiteServiceInfos(uuid: string) {
        return this.equatorServiceService.getLiteServiceInfos(uuid);
    }

    public getServiceToDeviceMap(uuid: string) {
        return this.equatorServiceService.getServiceToDeviceMap(uuid);
    }

    public getCHSecurityDevices(uuid: string) {
        return this.equatorDeviceService.getFilteredDevices(
            uuid,
            "(&(|(adapter=Clare Secure)(adapter=ClareOne))(!(parent.UID=*)))"
        ) as Observable<CHSecurityDevice[]>;
    }

    public getSecurityPanel(uuid: string): Observable<CHSecurityDevice[]> {
        if (uuid) {
            return this.getCHSecurityDevices(uuid).pipe(
                catchError(e => {
                    if (typeof e?.indexOf === "function" && e?.indexOf(`Post "https://`) === 0) {
                        return of([]);
                    }

                    throw e;
                })
            );
        }
        return of([]);
    }

    public getClareOnePartition(uuid: string) {
        return this.getSecurityPanel(uuid).pipe(
            map(securityPanels => securityPanels.find((panelDevice: CHDevice) => CHDeviceHelper.isClareOneDevice(panelDevice)))
        );
    }

    public verifySerialNumber(uuid: string, serialNumber: string): Observable<any> {
        return this.equatorClareHomeService.verifySerialNumber(uuid, serialNumber);
    }

    public updateServiceNameAndZone(uuid: string, instanceId: string, name: string, zoneUids: string[] = []) {
        return this.equatorServiceService.updateNameAndZone(uuid, instanceId, name, zoneUids);
    }

    public deleteService(uuid: string, service: CHService) {
        return this.equatorServiceService.deleteService(uuid, service.instanceId).pipe(map(_ => ({ uuid, result: service })));
    }

    public isDoorLockService(service: CHService) {
        return service.definitionId === "com.clarecontrols.widgets.doorlock";
    }

    public setUnits(service, conversionKey) {
        if (conversionKey && !service.units) {
            const converter = this.dataConverter.getConverter(conversionKey);
            service.units = {};
            service.units[conversionKey] = Object.assign({}, converter.defaultUnits);
            converter.defaultUnits.output = service.units[conversionKey].output;
        }
        return service.units;
    }
}
